import { useApolloClient } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { DateFormField, InputFormField } from '@repo/shared-client/components/fields'
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState, useWatch } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import { forwardRef, useRef, useState } from 'react'
import toast from 'react-hot-toast'

interface UserProps {
  id: string
  bannedUntil?: string
  banned?: boolean
  profile?: {
    deletedAt?: string
  }
}

interface BanningCardProps {
  user: UserProps
  className?: string
  onModerationUpdated?: () => void | Promise<void>
}

const BanningEdit = forwardRef<HTMLDivElement, BanningCardProps>((props, forwardedRef) => {
  const { t } = useTranslation()
  const formRef = useRef<HTMLFormElement>(null)
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [banModal, setBanModal] = useState(false)
  const [unbanModal, setUnbanModal] = useState(false)
  const { user, className, onModerationUpdated } = props
  const { banUser, unbanUser } = useAuth()
  const { cache } = useApolloClient()

  const formMethods = useForm({
    defaultValues: {
      action: 'none',
      banReason: '',
      bannedUntil: user?.bannedUntil ? new Date(user?.bannedUntil).toISOString() : null,
    },
  })

  const { isDirty } = useFormState({ control: formMethods.control })
  const banReason = useWatch({ control: formMethods.control, name: 'banReason', defaultValue: '' })
  const bannedUntil = useWatch({ control: formMethods.control, name: 'bannedUntil', defaultValue: null })

  const hasBanReason = Boolean(banReason?.trim())
  const hasBannedUntilDate = Boolean(bannedUntil)

  const handleSubmit = async (fieldData: { action: string; banReason: string; bannedUntil: string | null }) => {
    setShowSpinnerValue(true)
    setBanModal(false)
    setUnbanModal(false)

    let newBannedUntil: string | null = null
    let successMessage = ''

    try {
      if (fieldData.action === 'ban') {
        successMessage = t('User banned')
        newBannedUntil = fieldData.bannedUntil
        await banUser.mutateAsync({
          userId: user.id,
          banReason: fieldData.banReason,
          banExpiresIn: (new Date(fieldData.bannedUntil ?? '').getTime() - Date.now()) / 1000,
        })
      } else if (fieldData.action === 'banPermanent') {
        successMessage = t('User permanently banned')
        newBannedUntil = null
        await banUser.mutateAsync({
          userId: user.id,
          banReason: fieldData.banReason,
          banExpiresIn: undefined,
        })
      } else if (fieldData.action === 'banWeek') {
        successMessage = t('User temporarily banned')
        newBannedUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        await banUser.mutateAsync({
          userId: user.id,
          banReason: fieldData.banReason,
          banExpiresIn: 60 * 60 * 24 * 7,
        })
      } else if (fieldData.action === 'unban') {
        successMessage = t('User unbanned')
        newBannedUntil = null
        await unbanUser.mutateAsync({ userId: user.id })
      }

      try {
        cache.modify({
          id: cache.identify({ __typename: 'User', id: user.id }),
          fields: {
            banned: () => fieldData.action !== 'unban',
            bannedUntil: () => newBannedUntil,
          },
        })
      } catch (cacheError) {
        console.warn('Failed to update Apollo cache:', cacheError)
      }

      toast.success(successMessage)
      await onModerationUpdated?.()
    } catch (error) {
      console.error(error)
      toast.error(error instanceof Error ? error.message : t('Error'))
    } finally {
      setShowSpinnerValue(false)
    }
  }

  return (
    <Form formMethods={formMethods} onSubmit={handleSubmit} ref={formRef} style={{ width: '100%' }}>
      <div className={cn('flex w-full flex-col gap-4', className)}>
        <Card className="w-full" ref={forwardedRef}>
          <CardHeader>
            <CardTitle>{t('Banning')}</CardTitle>
          </CardHeader>
          <CardContent>
            {!user.banned && (
              <div className="flex w-full flex-col gap-4">
                <InputFormField label={t('Ban Reason')} name="banReason" placeholder={t('Enter ban reason')} required />
                <div className="flex w-full flex-col items-stretch gap-2 md:flex-row md:items-end">
                  <div className="flex-1">
                    <DateFormField
                      endYear={new Date().getFullYear() + 10}
                      fieldProps={{ readOnly: Boolean(user.banned) || Boolean(user?.profile?.deletedAt) }}
                      label={t('Banned Until')}
                      name="bannedUntil"
                      startYear={new Date().getFullYear()}
                    />
                  </div>
                  <Button
                    className="shrink-0 md:mt-0"
                    disabled={(Boolean(user?.profile?.deletedAt) && !isDirty) || showSpinner || !hasBannedUntilDate}
                    onClick={() => {
                      formMethods.setValue('action', 'ban')
                      setBanModal(true)
                    }}
                    type="button"
                    variant="secondary"
                  >
                    {showSpinner ? <Spinner className="size-4" /> : null}
                    Ban Until
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="flex w-full flex-col gap-2 self-end">
          {user.banned ? (
            <div className="flex w-full justify-end">
              <Button
                disabled={showSpinner}
                onClick={() => {
                  formMethods.setValue('action', 'unban')
                  setUnbanModal(true)
                }}
                type="button"
              >
                {showSpinner ? <Spinner className="size-4" /> : null}
                Unban User
              </Button>
            </div>
          ) : (
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <Button
                disabled={Boolean(user?.profile?.deletedAt) || showSpinner || !hasBanReason}
                onClick={() => {
                  formMethods.setValue('action', 'banWeek')
                  setBanModal(true)
                }}
                type="button"
                variant="secondary"
              >
                Temporary Ban
              </Button>
              <div className="hidden flex-1 md:block" />
              <Button
                disabled={Boolean(user?.profile?.deletedAt) || showSpinner || !hasBanReason}
                onClick={() => {
                  formMethods.setValue('action', 'banPermanent')
                  setBanModal(true)
                }}
                type="button"
              >
                Permanent Ban
              </Button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        confirmText={t('Ban')}
        onConfirm={() => formRef.current?.requestSubmit()}
        onOpenChange={(e) => setBanModal(e.open)}
        open={banModal}
        text={t('Are you sure you want to ban this user?')}
        title={t('Are you sure?')}
        variant="delete"
      />

      <ConfirmDialog
        confirmText={t('Unban')}
        onConfirm={() => formRef.current?.requestSubmit()}
        onOpenChange={(e) => setUnbanModal(e.open)}
        open={unbanModal}
        text={t('Are you sure you want to unban this user?')}
        title={t('Are you sure?')}
        variant="basic"
      />
    </Form>
  )
})

BanningEdit.displayName = 'BanningEdit'

export default BanningEdit
